/**
 * Generic MFA Utilities
 *
 * This module provides reusable MFA functions that can be used across different portals.
 * Each portal should implement its own specific auth.js that uses these utilities.
 */

import { setToken } from './auth';

/**
 * Generic MFA setup completion
 *
 * @param {string} apiEndpoint - The portal-specific MFA setup endpoint
 * @param {string} userSub - The cognito user sub
 * @param {string} session - The AWS Cognito session token
 * @param {string} totpCode - The 6-digit TOTP code
 * @param {function} onSuccess - Callback function on success (receives token data)
 * @param {function} onError - Callback function on error (receives error)
 * @returns {Promise}
 */
export const completeMFASetup = async (apiEndpoint, userSub, session, totpCode, onSuccess, onError) => {
  try {
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ user_sub: userSub, session, totp_code: totpCode }),
    });

    let body;
    try {
      body = await response.json();
    } catch (jsonError) {
      // If response is not JSON, it's likely a token/auth error
      const error = new Error('InvalidTokenSupplied');
      error.details = 'Invalid or expired token';
      error.code = 'InvalidTokenSupplied';
      throw error;
    }

    if (!response.ok) {
      const error = new Error((body.error && body.error.code) || 'mfa-setup-failed');
      error.details = body.error && body.error.message;
      error.code = body.error && body.error.code;
      throw error;
    }

    // Store the new token
    if (body.token && body.exp) {
      setToken(body.token, body.exp);
    }

    if (onSuccess) {
      onSuccess(body);
    }

    return body;
  } catch (error) {
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

/**
 * Generic MFA verification
 *
 * @param {string} apiEndpoint - The portal-specific MFA verify endpoint
 * @param {string} userSub - The cognito user sub
 * @param {string} totpCode - The 6-digit TOTP code
 * @param {function} onSuccess - Callback function on success (receives token data)
 * @param {function} onError - Callback function on error (receives error)
 * @returns {Promise}
 */
export const verifyMFA = async (apiEndpoint, userSub, totpCode, onSuccess, onError) => {
  try {
    const response = await fetch(apiEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ user_sub: userSub, totp_code: totpCode }),
    });

    let body;
    try {
      body = await response.json();
    } catch (jsonError) {
      // If response is not JSON, it's likely a token/auth error
      const error = new Error('InvalidTokenSupplied');
      error.details = 'Invalid or expired token';
      error.code = 'InvalidTokenSupplied';
      throw error;
    }

    if (!response.ok) {
      const error = new Error((body.error && body.error.code) || 'mfa-verification-failed');
      error.details = body.error && body.error.message;
      error.code = body.error && body.error.code;
      throw error;
    }

    // Store the new token
    if (body.token && body.exp) {
      setToken(body.token, body.exp);
    }

    if (onSuccess) {
      onSuccess(body);
    }

    return body;
  } catch (error) {
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

/**
 * Generate TOTP URI for QR code generation
 *
 * @param {string} secretCode - The TOTP secret code
 * @param {string} username - The username for the account
 * @param {string} issuer - The issuer name (e.g., "BPS Cardholder Portal")
 * @returns {string} The TOTP URI
 */
export const generateTOTPUri = (secretCode, username = 'User', issuer = 'BPS Portal') => `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(username)}?secret=${secretCode}&issuer=${encodeURIComponent(issuer)}`;

/**
 * MFA session storage utilities
 * These help manage MFA state across page refreshes
 */
export const MFAStorage = {
  // Store MFA setup data (for setup flow)
  setSetupData: (data) => {
    sessionStorage.setItem('mfa_setup_data', JSON.stringify(data));
  },

  // Get MFA setup data
  getSetupData: () => {
    try {
      const data = sessionStorage.getItem('mfa_setup_data');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      // Silent catch for JSON parsing errors
      return null;
    }
  },

  // Store user sub (for verify flow)
  setUserSub: (userSub) => {
    sessionStorage.setItem('mfa_user_sub', userSub);
  },

  // Get user sub
  getUserSub: () => sessionStorage.getItem('mfa_user_sub'),

  // Clear all MFA session data
  clearAll: () => {
    sessionStorage.removeItem('mfa_setup_data');
    sessionStorage.removeItem('mfa_user_sub');
  },

  // Clear only setup data
  clearSetupData: () => {
    sessionStorage.removeItem('mfa_setup_data');
  },

  // Clear only user sub
  clearUserSub: () => {
    sessionStorage.removeItem('mfa_user_sub');
  }
};

/**
 * MFA error classification utilities
 */
export const MFAErrors = {
  // Check if error is authentication-related (should redirect to login)
  isAuthError: (error) => {
    const code = error.code || '';

    // Simplified check using standardized error codes
    return code === 'invalid_credentials' ||
           code === 'InvalidTokenSupplied' ||
           code === 'invalid_token' ||
           code === 'token_expired' ||
           code === 'token_invalid';
  },

  // Get user-friendly error message
  // Now uses standardized error codes from bps-go → bps-ex flow
  getUserFriendlyError: (error) => {
    const code = error.code || '';

    // Direct mapping of standardized error codes from backend
    switch (code) {
    case 'invalid_totp_code':
      return 'errors-invalid-totp-code';

    case 'mfa_not_setup':
      return 'errors-mfa-not-setup';

    case 'mfa_session_invalid':
    case 'mfa_session_expired':
      return 'errors-mfa-session-error';

    case 'user_not_found':
      return 'errors-user-not-found';

    case 'too_many_attempts':
      return 'errors-too-many-attempts';

    case 'rate_limited':
      return 'errors-rate-limited';

    case 'invalid_credentials':
      return 'errors-unauthorized';

    case 'mfa_verification_failed':
      return 'errors-mfa-verification-failed';

    case 'mfa_setup_failed':
      return 'errors-mfa-setup-failed';

    case 'internal_error':
      return 'errors-internal-error';

    default:
      return 'errors-unexpected';
    }
  }
};

/**
 * Validation utilities
 */
export const MFAValidation = {
  // Validate TOTP code format
  isValidTOTPCode: (code) => typeof code === 'string' && /^\d{6}$/.test(code),

  // Validate MFA setup data
  isValidSetupData: (data) => data &&
           typeof data.secret_code === 'string' &&
           typeof data.session === 'string' &&
           typeof data.user_sub === 'string' &&
           data.secret_code.length > 0 &&
           data.session.length > 0 &&
           data.user_sub.length > 0,

  // Validate user sub
  isValidUserSub: (userSub) => typeof userSub === 'string' && userSub.length > 0
};